import http from '@/core/http';
import { defineStore } from 'pinia';
import { IState } from './type';
import { useInstances } from '@/hooks/use-instances';
import { authLogout, authReportAction, authTenantLogin } from '@/api/uacs/auth';
import { userFindTenants, userMe } from '@/api/uacs/user';
import { useRoute } from 'vue-router';
import { TokenCookie } from '@toy/utils';
import { currentEnv } from '@/core/http/env';
import { ACCESS_TYPE_ENUM } from '@/api/uacs/auth/constant';
import { CURRENT_CLIENT_CODE } from '@/constant';
import { ElMessage } from 'element-plus';

const { getVueRouterInstance } = useInstances();
const router = getVueRouterInstance();

const tokenCookie = new TokenCookie(currentEnv);

const initToken = () => {
  const token = tokenCookie.getToken();
  if (token) {
    http.setHeader({
      Authorization: `Bearer ${token}`,
    });
  }
  return token || '';
};

export const useAccountStore = defineStore({
  id: 'account',
  state: (): IState => ({
    account: null,
    token: initToken(),
    tenantList: [],
    loginSystem: false,
    currentTenantId: '',
    selectedTenant: null,
  }),
  getters: {
    isInnerClient: () => {
      const route = useRoute();
      return !!(route.query?.inner || !route.query?.clientCode);
    },
  },
  // 定义修改state数据函数
  actions: {
    updateLoginSystem(val: boolean) {
      this.loginSystem = val;
    },
    async getTenantList() {
      if (this.tenantList.length > 0) return;
      const { data } = await userFindTenants();
      this.tenantList = data || [];
    },
    setTenantList(list: IState['tenantList']) {
      this.tenantList = list;
    },
    getDefaultTenant() {
      if (this.tenantList?.length === 1) {
        return this.tenantList[0];
      }
      return null;
    },
    // 设置当前租户
    async setCurrentTenant({
      tenantId,
      cb,
      orgId,
    }: { tenantId: string; cb?: () => void; orgId?: string; }) {
      this.selectedTenant = {
        tenantId,
        orgId,
      };
      try {
        const authTenantRes = await authTenantLogin({
          tenantId,
          organizationId: orgId,
        });
        this.currentTenantId = tenantId;

        this.clearCache();
        this.setToken(authTenantRes.data.token);
        cb && cb();
      } catch (error) {
        const message = (error as any)?.message || '登录失败';
        if ((error as any).code === 500102) {
          this.selectedTenant.errorMessage = message;
          // 不是会话处理页面，则跳转到会话处理页面
          if (router.currentRoute.value.name !== 'SessionManage') {
            router.push({ name: 'SessionManage', query: router.currentRoute.value.query });
          }
        } else {
          ElMessage.error((error as any)?.message || '登录失败');
        }
      }
    },
    setToken(token: string) {
      this.token = token;
      tokenCookie.setToken(token);
      http.setHeader({
        Authorization: `Bearer ${token}`,
      });
    },
    // 获取用户信息
    async getAccountInfo() {
      try {
        if (this.account) return;
        const { data } = await userMe();
        this.account = data;
      } catch (e) {
        console.log(e);
        throw Error('获取用户信息失败');
      }
    },
    async logout(isExpiration = false) {
      if (!isExpiration) {
        try {
          await authReportAction({
            accessType: ACCESS_TYPE_ENUM.LOGOUT,
            clientCode: CURRENT_CLIENT_CODE,
          });
          await authLogout();
        } catch (error) {
          console.log('error', error);
        }
      }
      this.clearCache();
      if (router.currentRoute.value.name === 'Login') return;
      const hasRedirect = router.currentRoute.value.query.redirect;
      // 这里要判断一下。当前地址 不能是选择租户页
      const hasSelectTenant = window.location.pathname.includes('/select-tenant') 
        || window.location.pathname.includes('/session-manage');
      //  这里如果有 redirect 参数，则直接设置 redirect 参数，否则直接跳转到登陆页面
      const query = hasRedirect ? router.currentRoute.value.query : {
        redirect: hasSelectTenant ? undefined : window.location.pathname,
      };
      router.replace({
        path: '/login',
        query,
      });
    },
    // 清除所有缓存信息
    clearCache() {
      this.clearToken();
      this.account = null;
      this.tenantList = [];
    },
    // 清除token
    clearToken() {
      // 如果本地token 和 状态管理的token 不一致，则不执行清除操作
      const localToken = tokenCookie.getToken();
      if (localToken !== this.token) {
        // 设置状态管理token
        this.setToken(localToken || '');
        return;
      }
      tokenCookie.removeToken();
      this.token = null;
      http.setHeader({
        Authorization: '',
      });
    },
  },
});

http.setUnauthorizedCallback(async () => {
  await useAccountStore().logout(true);
});
